import React, { FC, useCallback, useEffect, useState } from "react";

import {
    GoogleBusinessProfile,
    LoadingSpinner,
    OnboardingAside,
    OnboardingContent,
    Place,
    PlaceList,
    PrimaryAlternateControl,
} from "@xapp/components";

// import { OnboardingProps } from "../../models";

export interface FindAndConfirmProfileStepProps {
    /**
     * Optional style applied to the root div
     */
    style?: React.CSSProperties;
    /**
     * Optional class name to apply to the root div
     */
    className?: string;
    /**
     * Optional aside content
     */
    aside?: React.ReactNode;
    /**
     * Whether the business is loading
     */
    loading: boolean;

    place?: Place;
    /**
     * What was searched to find the results
     */
    searchTerms?: string;
    /**
     * Results for the user to select a place from
     */
    results?: Place[];
    /**
     * Will get more details about the place
     *
     */
    onSelect?: (place: Place) => void;

    onNext: (place?: Place) => void;

    onBack: () => void;
}

export const FindAndConfirmProfileStep: FC<FindAndConfirmProfileStepProps> = (props) => {
    const { aside, loading, place, searchTerms, results, onNext, onBack, onSelect } = props;

    const [placeToConfirm, setPlaceToConfirm] = useState<Place>(place);

    useEffect(() => {
        // if a place now all of a sudden has a maps URL and the previous does not
        if ((place && !placeToConfirm) || placeToConfirm?.mapsUrl !== place?.mapsUrl) {
            setPlaceToConfirm(place);
        }
    }, [place, placeToConfirm]);

    // console.log("FindAndConfirmProfileStep", props);
    // console.log("FindAndConfirmProfileStep place", place);
    // console.log("FindAndConfirmProfileStep placeToConfirm", placeToConfirm);

    const handlePrimary = useCallback(() => {
        if (place) {
            onNext(place);
        }
    }, [onNext, place]);

    const handleSelect = useCallback(
        (place: Place) => {
            if (typeof onSelect === "function") {
                onSelect(place);
            }
            setPlaceToConfirm(place);
        },
        [onSelect]
    );

    const handleNoPlace = useCallback(() => {
        if (typeof onSelect === "function") {
            onNext(undefined);
        }
    }, [onNext, onSelect]);

    const handleShowResults = useCallback(() => {
        if (typeof onSelect === "function") {
            onSelect(undefined);
        }
        setPlaceToConfirm(undefined);
    }, [onSelect]);

    const showAlternate = results && results.length > 1;

    return (
        <>
            {aside && <OnboardingAside>{aside}</OnboardingAside>}

            <OnboardingContent>
                {loading ? (
                    <LoadingSpinner loadingText="Loading place details..." />
                ) : (
                    <>
                        {placeToConfirm ? (
                            <>
                                {searchTerms && (
                                    <>
                                        <small>We searched using...</small>
                                        <i>{searchTerms}</i>
                                    </>
                                )}

                                <GoogleBusinessProfile
                                    loading={loading || !placeToConfirm}
                                    place={placeToConfirm}
                                    showContact={true}
                                />

                                <h3>Is this your business?</h3>

                                <PrimaryAlternateControl
                                    labels={{
                                        primary: "Yes",
                                        alternate: showAlternate ? "No, show the results" : "No",
                                    }}
                                    onPrimary={handlePrimary}
                                    onAlternate={showAlternate ? handleShowResults : handleNoPlace}
                                />
                            </>
                        ) : (
                            <>
                                {searchTerms && (
                                    <>
                                        <small>We searched using...</small>
                                        <i>{searchTerms}</i>
                                    </>
                                )}

                                <div style={{ height: "300px", width: "100%", overflowY: "auto", padding: "15px" }}>
                                    <PlaceList places={results} onSelect={handleSelect} />
                                </div>
                                <h3>Is one of the above your business?</h3>

                                <p>
                                    Select your business above or if you don't see it, we can move on to the next step.
                                </p>

                                <PrimaryAlternateControl
                                    labels={{
                                        primary: "No, I don't see it",
                                        alternate: "Back",
                                    }}
                                    onPrimary={handleNoPlace}
                                    onAlternate={onBack}
                                />
                            </>
                        )}
                    </>
                )}
            </OnboardingContent>
        </>
    );
};
